import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface MenuAction {
  label: string; // Display text, e.g. "Share"
  icon?: React.ReactNode; // Icon element (e.g. <Share2 />)
  onClick: () => void; // Function to run on click
  color?: string; // Optional text color override (e.g. "text-red-600")
  hoverColor?: string; // Optional hover background override (e.g. "hover:bg-red-50")
}

interface DropdownMenuProps {
  actions: MenuAction[];
  className?: string;
  triggerClassName?: string;
  children?: React.ReactNode;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  actions,
  className = '',
  triggerClassName = '',
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      // Check if click is outside the trigger button
      if (menuRef.current && !menuRef.current.contains(target)) {
        // Check if click is inside the dropdown (portal)
        const dropdownElement = document.querySelector('[data-dropdown-menu]');
        if (!dropdownElement || !dropdownElement.contains(target)) {
          setOpen(false);
        }
      }
    };

    // Close menu on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [open]);

  const handleTriggerClick = () => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.right - 192,
      });
    }
    setOpen(!open);
  };

  const handleActionClick = (action: MenuAction) => {
    action.onClick();
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={handleTriggerClick}
        className={`p-1 hover:bg-gray-100 transition-colors ${triggerClassName || 'rounded-md'}`}
        aria-label="More options"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {children || '⋯'}
      </button>

      {/* Dropdown Menu - Portal to escape stacking context */}
      {open &&
        createPortal(
          <div
            data-dropdown-menu
            className="fixed bg-white border border-gray-200 shadow-lg rounded-lg py-1 z-[9999]"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: '192px',
            }}
          >
            {actions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleActionClick(action)}
                className={`
                flex items-center gap-3 w-full text-sm px-3 py-2 text-left transition-colors
                ${action.color || 'text-gray-700'}
                ${action.hoverColor || 'hover:bg-gray-50'}
                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
              `}
              >
                {action.icon && (
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {action.icon}
                  </span>
                )}
                <span className="truncate">{action.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DropdownMenu;
